/**
 * Converts Dust webapp URLs to their poke equivalents
 * Returns null if no poke equivalent exists
 */

interface RouteMapping {
  pattern: RegExp;
  pokePath: string | null;
}

const ROUTE_MAPPINGS: RouteMapping[] = [
  // Conversations (old /assistant/, /agent/, and new /conversation/ URLs)
  {
    pattern: /^\/w\/([^/]+)\/assistant\/([^/]+)$/,
    pokePath: "/poke/$1/conversation/$2",
  },
  {
    pattern: /^\/w\/([^/]+)\/agent\/([^/]+)$/,
    pokePath: "/poke/$1/conversation/$2",
  },
  {
    pattern: /^\/w\/([^/]+)\/conversation\/([^/]+)$/,
    pokePath: "/poke/$1/conversation/$2",
  },

  // Assistants
  {
    pattern: /^\/w\/([^/]+)\/builder\/assistants\/([^/]+)$/,
    pokePath: "/poke/$1/assistants/$2",
  },

  // Spaces
  {
    pattern: /^\/w\/([^/]+)\/spaces\/([^/]+)$/,
    pokePath: "/poke/$1/spaces/$2",
  },

  // Apps
  {
    pattern: /^\/w\/([^/]+)\/spaces\/([^/]+)\/apps\/([^/]+)$/,
    pokePath: "/poke/$1/spaces/$2/apps/$3",
  },

  // App runs
  {
    pattern: /^\/w\/([^/]+)\/spaces\/([^/]+)\/apps\/([^/]+)\/runs$/,
    pokePath: "/poke/$1/spaces/$2/apps/$3",
  },

  // Specific app run
  {
    pattern: /^\/w\/([^/]+)\/spaces\/([^/]+)\/apps\/([^/]+)\/runs\/([^/]+)$/,
    pokePath: "/poke/$1/spaces/$2/apps/$3",
  },

  // Data source views in spaces/categories
  {
    pattern:
      /^\/w\/([^/]+)\/spaces\/([^/]+)\/categories\/[^/]+\/data_source_views\/([^/]+)$/,
    pokePath: "/poke/$1/spaces/$2/data_source_views/$3",
  },

  // MCP server views
  {
    pattern: /^\/w\/([^/]+)\/spaces\/([^/]+)\/mcp_server_views\/([^/]+)$/,
    pokePath: "/poke/$1/spaces/$2/mcp_server_views/$3",
  },

  // Workspace root
  {
    pattern: /^\/w\/([^/]+)$/,
    pokePath: "/poke/$1",
  },

  // Workspace/memberships
  {
    pattern: /^\/w\/([^/]+)\/workspace$/,
    pokePath: "/poke/$1",
  },

  // Members
  {
    pattern: /^\/w\/([^/]+)\/members$/,
    pokePath: "/poke/$1/memberships",
  },

  // Data sources (legacy)
  {
    pattern: /^\/w\/([^/]+)\/data_sources\/([^/]+)$/,
    pokePath: "/poke/$1/data_sources/$2",
  },

  // Routes without poke equivalents
  {
    pattern: /^\/w\/([^/]+)\/join$/,
    pokePath: null,
  },
  {
    pattern: /^\/w\/([^/]+)\/subscription$/,
    pokePath: null,
  },
  {
    pattern: /^\/w\/([^/]+)\/oauth\//,
    pokePath: null,
  },
  {
    pattern: /^\/w\/([^/]+)\/builder\/data_sources/,
    pokePath: null,
  },
  {
    pattern: /^\/w\/([^/]+)\/spaces\/([^/]+)\/categories\//,
    pokePath: null,
  },
  {
    pattern:
      /^\/w\/([^/]+)\/spaces\/([^/]+)\/apps\/([^/]+)\/(settings|specification|datasets)/,
    pokePath: null,
  },
  {
    pattern: /^\/w\/([^/]+)\/labs\/(transcripts|mcp_actions)/,
    pokePath: null,
  },
];

export function convertUrlToPoke(url: string): string | null {
  try {
    const urlObj = new URL(url);

    // Check if it's a Counso domain (counso.ai or any subdomain like eu.counso.ai) or localhost
    // Must be exactly counso.ai or *.counso.ai (not *counso.ai which would match notcounso.ai)
    const isValidHostname =
      urlObj.hostname === "counso.ai" ||
      urlObj.hostname.endsWith(".counso.ai") ||
      urlObj.hostname === "localhost" ||
      urlObj.hostname === "127.0.0.1";

    if (!isValidHostname) {
      return null;
    }

    const pathname = urlObj.pathname;

    // Find matching route
    for (const mapping of ROUTE_MAPPINGS) {
      const match = pathname.match(mapping.pattern);
      if (match) {
        if (mapping.pokePath === null) {
          return null;
        }

        // Replace placeholders with captured groups
        let pokePath = mapping.pokePath;
        for (let i = 1; i < match.length; i++) {
          pokePath = pokePath.replace(`$${i}`, match[i]);
        }

        // Construct new URL with poke path
        const pokeUrl = new URL(urlObj);
        pokeUrl.pathname = pokePath;

        // Remove query params that might not be relevant in poke
        pokeUrl.search = "";

        return pokeUrl.toString();
      }
    }

    // No match found
    return null;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
  } catch (error) {
    // Invalid URL
    return null;
  }
}
